/**
 * Importación de Ventas al Contado desde otros sistemas.
 *
 * Una plantilla Excel, una fila por venta (un chasis): Fecha, Cedula,
 * Nombres, Apellidos, Direccion, Telefono, Celular, Provincia, Email, Chasis,
 * Marca, Modelo, Color, Costo, Precio, Forma de pago, Referencia (+ Empresa
 * con multi-empresa).
 *
 * Flujo (igual que la importación de ventas x mayor): subir el archivo ->
 * "Validar" llama /api/ventacontado_importar_validar (sin escribir nada) y
 * muestra errores/avisos/vista previa -> "Importar" llama
 * /api/ventacontado_importar_guardar, que revalida y graba todo en una
 * transacción (si una fila falla no se importa ninguna).
 *
 * Un chasis que no exista en el inventario se crea ya vendido con los datos
 * de la fila, en la sucursal elegida aquí; uno existente conserva su
 * sucursal, como la venta manual.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { DataGrid } from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import { FP, PageHeader, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMultiempresa } from "lib/multiempresa";
import { documentoValido, formatDocumentoAuto, tipoDocumento } from "lib/documento";
import { parseExcelPreview, downloadTemplateXlsx } from "./excelPreview";

const COLUMNAS = [
  "Fecha",
  "Cedula",
  "Nombres",
  "Apellidos",
  "Direccion",
  "Telefono",
  "Celular",
  "Provincia",
  "Email",
  "Chasis",
  "Marca",
  "Modelo",
  "Color",
  "Costo",
  "Precio",
  "Forma de pago",
  "Referencia",
];
// Multi-empresa: columna opcional al final para los chasis nuevos.
const COLUMNA_EMPRESA = "Empresa";
const ARCHIVO = "plantilla_ventas_contado.xlsx";

function normalizarEncabezado(valor) {
  return String(valor ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Las columnas deben ser exactamente las de la plantilla; "Empresa" puede
 *  venir o no al final. */
function encabezadosCoinciden(actuales) {
  if (!Array.isArray(actuales)) return false;
  const sinEmpresa = actuales.filter(
    (h) => normalizarEncabezado(h) !== normalizarEncabezado(COLUMNA_EMPRESA)
  );
  return (
    sinEmpresa.length === COLUMNAS.length &&
    COLUMNAS.every((c, i) => normalizarEncabezado(sinEmpresa[i]) === normalizarEncabezado(c))
  );
}

/** Cédula/RNC con su máscara cuando el largo es válido; otro largo va tal
 *  cual (mismo criterio de la importación de préstamos). */
function conDocumentoFormateado(fila) {
  const clave = Object.keys(fila).find((k) => normalizarEncabezado(k) === "cedula");
  if (!clave) return fila;
  const valor = String(fila[clave] ?? "").trim();
  if (!valor) return fila;
  return {
    ...fila,
    [clave]: documentoValido(valor, tipoDocumento(valor)) ? formatDocumentoAuto(valor) : valor,
  };
}

function archivoVacio() {
  return { fileName: null, rows: [], rowCount: 0, error: null, loading: false };
}

const fmtMoneda = new Intl.NumberFormat("es-DO", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default function ImportarVentasContado() {
  const { post } = useApi();
  const multiempresa = useMultiempresa();

  const [archivo, setArchivo] = useState(archivoVacio());
  const [sucursales, setSucursales] = useState([]);
  const [sucursal, setSucursal] = useState(null);
  const [validacion, setValidacion] = useState(null);
  const [validando, setValidando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");
  const [confirmarOpen, setConfirmarOpen] = useState(false);
  const [resultado, setResultado] = useState(null);
  const inputRef = useRef(null);
  // Candado contra doble clic: el estado tarda un render en desactivar el botón.
  const importandoRef = useRef(false);

  useEffect(() => {
    post("/api/recuperasucursales", { sucursal: " " })
      .then(({ data }) => {
        const lista = Array.isArray(data?.sucursales) ? data.sucursales : [];
        setSucursales(lista);
        setSucursal((prev) => prev ?? lista[0] ?? null);
      })
      .catch(() => setSucursales([]));
  }, [post]);

  const columnasPlantilla = useMemo(
    () => (multiempresa.activo ? [...COLUMNAS, COLUMNA_EMPRESA] : COLUMNAS),
    [multiempresa.activo]
  );

  const limpiarResultados = () => {
    setValidacion(null);
    setResultado(null);
    setErrorGeneral("");
  };

  const handleArchivo = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    limpiarResultados();
    setArchivo((prev) => ({ ...prev, loading: true, error: null }));
    try {
      // maxPreviewRows alto: aquí las filas parseadas SON las que se importan.
      const r = await parseExcelPreview(file, { maxPreviewRows: 100000 });
      const okHeaders = encabezadosCoinciden(r.headers);
      setArchivo({
        fileName: r.fileName,
        rows: okHeaders ? r.rows.map(({ id, ...resto }) => conDocumentoFormateado(resto)) : [],
        rowCount: r.rowCount,
        loading: false,
        error: okHeaders
          ? null
          : `Las columnas no coinciden con la plantilla. Deben ser exactamente: ${COLUMNAS.join(
              ", "
            )}.`,
      });
    } catch (err) {
      setArchivo({ ...archivoVacio(), error: err?.message || "Error al leer el archivo." });
    }
  }, []);

  const handleQuitar = useCallback(() => {
    setArchivo(archivoVacio());
    limpiarResultados();
  }, []);

  const cuerpo = useMemo(
    () => ({
      ventas: archivo.rows,
      sucursal: typeof sucursal === "string" ? sucursal : sucursal?.label || "",
    }),
    [archivo.rows, sucursal]
  );

  const handleValidar = useCallback(async () => {
    setValidando(true);
    limpiarResultados();
    try {
      const { ok, status, data } = await post("/api/ventacontado_importar_validar", cuerpo);
      if (!ok || data?.Error) {
        throw new Error(data?.Error || `No se pudo validar la importación (HTTP ${status}).`);
      }
      setValidacion(data);
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo validar la importación.");
    } finally {
      setValidando(false);
    }
  }, [post, cuerpo]);

  const handleImportar = useCallback(async () => {
    if (importandoRef.current) return;
    importandoRef.current = true;
    setConfirmarOpen(false);
    setImportando(true);
    setErrorGeneral("");
    try {
      const { ok, status, data } = await post("/api/ventacontado_importar_guardar", cuerpo);
      if (!ok || data?.Error) {
        const detalle =
          Array.isArray(data?.errores) && data.errores.length ? ` ${data.errores.join(" | ")}` : "";
        throw new Error((data?.Error || `No se pudo importar (HTTP ${status}).`) + detalle);
      }
      setResultado(data);
      setValidacion(null);
      setArchivo(archivoVacio());
    } catch (e) {
      setErrorGeneral(e?.message || "No se pudo importar.");
    } finally {
      importandoRef.current = false;
      setImportando(false);
    }
  }, [post, cuerpo]);

  const columnasPrevia = useMemo(
    () => [
      { field: "fila", headerName: "Fila", width: 70 },
      { field: "fecha", headerName: "Fecha", width: 110 },
      { field: "cedula", headerName: "Documento", width: 140 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
      { field: "chasis", headerName: "Chasis", width: 170 },
      { field: "vehiculo", headerName: "Vehículo", flex: 1, minWidth: 160 },
      {
        field: "nuevo",
        headerName: "Inventario",
        width: 120,
        renderCell: (p) =>
          p.value ? (
            <Chip size="small" color="info" label="Se creará" />
          ) : (
            <Chip size="small" label="Existente" />
          ),
      },
      { field: "sucursal", headerName: "Sucursal", width: 90 },
      { field: "forma", headerName: "Forma de pago", width: 130 },
      {
        field: "precio",
        headerName: "Precio",
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
    ],
    []
  );

  const columnasResultado = useMemo(
    () => [
      { field: "fila", headerName: "Fila", width: 70 },
      { field: "factura", headerName: "Factura", width: 110 },
      { field: "chasis", headerName: "Chasis", width: 180 },
      { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
      {
        field: "precio",
        headerName: "Precio",
        width: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (p) => fmtMoneda.format(p.value || 0),
      },
    ],
    []
  );

  const resumen = validacion?.resumen;
  const listoParaValidar = archivo.rows.length > 0 && !archivo.error && sucursal != null;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Importar Ventas al Contado"
        subtitle="Importa ventas al contado de otro sistema desde una plantilla Excel"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: "none" }}
          onChange={handleArchivo}
        />

        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Plantilla de ventas al contado
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Una fila por venta (un chasis). Columnas: {columnasPlantilla.join(", ")}.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Obligatorias: Fecha, Cedula, Nombres, Apellidos, Chasis y Precio. Los demás datos del
            cliente son opcionales. Si el chasis no existe en el inventario se crea ya vendido con
            Marca, Modelo, Color y Costo de la fila (Marca y Modelo obligatorias en ese caso) en la
            sucursal elegida abajo; si existe, se usan sus datos y su sucursal. Forma de pago:
            Contado, Transferencias o Tarjeta (vacía = Contado). La venta queda con la fecha del
            archivo, así que aparece en el cuadre de ese día.
            {multiempresa.activo &&
              ' La columna "Empresa" (código) es opcional y solo aplica a chasis nuevos; vacía = empresa 1.'}
          </Alert>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5} lg={4}>
              <Autocomplete
                value={sucursal}
                onChange={(_, v) => {
                  setSucursal(v);
                  setValidacion(null);
                }}
                options={sucursales}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : o?.label || o?.descripcion || ""
                }
                isOptionEqualToValue={(o, v) =>
                  (o?.id ?? o?.label ?? o) === (v?.id ?? v?.label ?? v)
                }
                noOptionsText="No hay sucursales disponibles"
                disabled={importando}
                renderInput={(params) => (
                  <TextField {...params} label="Sucursal para chasis nuevos" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={7} lg={8}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() => downloadTemplateXlsx(ARCHIVO, columnasPlantilla)}
                >
                  Plantilla
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<UploadFileOutlinedIcon />}
                  disabled={archivo.loading || importando}
                  onClick={() => inputRef.current?.click()}
                >
                  {archivo.fileName ? "Cambiar archivo" : "Elegir archivo"}
                </MDButton>
                {archivo.fileName && (
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={handleQuitar}
                    disabled={importando}
                  >
                    Quitar
                  </MDButton>
                )}
                {archivo.fileName && (
                  <Chip label={archivo.fileName} variant="outlined" size="small" />
                )}
                {archivo.rowCount > 0 && !archivo.error && (
                  <Chip
                    label={`${archivo.rowCount} fila${archivo.rowCount === 1 ? "" : "s"}`}
                    color="primary"
                    size="small"
                  />
                )}
                {archivo.loading && <CircularProgress size={20} />}
              </Stack>
            </Grid>
          </Grid>
          {archivo.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {archivo.error}
            </Alert>
          )}
        </Paper>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<FactCheckOutlinedIcon />}
            disabled={!listoParaValidar || validando || importando}
            onClick={handleValidar}
          >
            {validando ? "Validando..." : "Validar archivo"}
          </MDButton>
          {validacion?.valido && (
            <MDButton
              variant="gradient"
              color="success"
              disabled={importando}
              onClick={() => setConfirmarOpen(true)}
            >
              {importando ? "Importando..." : "Importar"}
            </MDButton>
          )}
          {importando && <CircularProgress size={22} />}
        </Stack>

        {errorGeneral && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorGeneral}
          </Alert>
        )}

        {validacion && (
          <>
            <Alert severity={validacion.valido ? "success" : "error"} sx={{ mb: 2 }}>
              <AlertTitle>
                {validacion.valido
                  ? "Validación correcta: todo listo para importar"
                  : "La importación tiene errores; corrija el Excel y vuelva a cargarlo"}
              </AlertTitle>
              {resumen && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Chip size="small" label={`${resumen.ventas} ventas`} />
                  <Chip size="small" label={`${resumen.chasisNuevos} chasis nuevos`} />
                  <Chip size="small" label={`Total ${fmtMoneda.format(resumen.montoTotal || 0)}`} />
                  {Object.entries(resumen.porForma || {}).map(([forma, monto]) => (
                    <Chip
                      key={forma}
                      size="small"
                      variant="outlined"
                      label={`${forma} ${fmtMoneda.format(monto || 0)}`}
                    />
                  ))}
                </Stack>
              )}
            </Alert>

            {validacion.errores?.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Errores ({validacion.errores.length})</AlertTitle>
                {validacion.errores.map((e, i) => (
                  <Typography key={`err-${i}`} variant="body2">
                    • {e}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.avisos?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Avisos ({validacion.avisos.length})</AlertTitle>
                {validacion.avisos.map((a, i) => (
                  <Typography key={`avi-${i}`} variant="body2">
                    • {a}
                  </Typography>
                ))}
              </Alert>
            )}

            {validacion.ventas?.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                <DataGrid
                  autoHeight
                  rows={validacion.ventas}
                  getRowId={(r) => r.fila}
                  columns={columnasPrevia}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={fpDataGridSx}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
              </Paper>
            )}
          </>
        )}

        {resultado && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>{resultado.ok}</AlertTitle>
              {resultado.chasisCreados > 0 && (
                <Typography variant="body2">
                  Chasis creados en el inventario: {resultado.chasisCreados}.
                </Typography>
              )}
            </Alert>
            {resultado.ventas?.length > 0 && (
              <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
                <DataGrid
                  autoHeight
                  rows={resultado.ventas}
                  getRowId={(r) => r.fila}
                  columns={columnasResultado}
                  density="compact"
                  disableRowSelectionOnClick
                  sx={fpDataGridSx}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

      <Dialog open={confirmarOpen} onClose={() => setConfirmarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar importación</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se importarán {resumen?.ventas ?? 0} ventas al contado
            {resumen?.chasisNuevos
              ? ` y se crearán ${resumen.chasisNuevos} chasis en el inventario`
              : ""}
            . La operación es todo o nada: si alguna fila falla, no se importa ninguna. ¿Desea
            continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="dark" onClick={() => setConfirmarOpen(false)}>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="success" onClick={handleImportar}>
            Importar
          </MDButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
